import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { SideSheet } from "../../components/sheet/sideSheet";
import { TextInput } from "../../components/textInput";
import { TextArea } from "../../components/textArea";
import { Select } from "../../components/select";
import { Checkbox } from "../../components/checkbox";
import { Toast } from "../../components/toast";
import { Files, uploadFile } from "../files";
import { PostActivities } from "./postActivities";
import { PostCoverage } from "./postCoverage";

const schema = z.object({
  post_name: z.string().trim().min(1, "Name is required").max(100),
  post_type: z.enum(["fixed", "roving", "mobile"]),
  description: z.string().optional(),
  is_armed: z.boolean().optional(),
  orders: z.string().optional(),
});

const SITE_POST_TYPES = [
  { value: "fixed", label: "Fixed (stationed in one spot)" },
  { value: "roving", label: "Roving (patrols this site)" },
];

// Create/edit sheet for a post. `post` is the post being edited, or null to
// create one. Site posts need `siteId`; mobile patrol routes pass `mobile`.
function PostDetails({ isOpen, onClose, post, siteId, mobile, onSaved }) {
  const authFetch = useAuthFetch();
  const isAddMode = !post;
  const noun = mobile ? "patrol route" : "post";

  const methods = useForm({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      post_name: "",
      post_type: mobile ? "mobile" : "fixed",
      description: "",
      is_armed: false,
      orders: "",
    },
  });
  const { reset, handleSubmit } = methods;

  const [ordersInfo, setOrdersInfo] = useState(null);
  // Files picked while creating; uploaded once the post has an id.
  const [pendingFiles, setPendingFiles] = useState([]);
  const [postActivities, setPostActivities] = useState([]);
  const [coverage, setCoverage] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setPendingFiles([]);
    setPostActivities([]);
    setCoverage([]);
    const values = {
      post_name: post?.name || "",
      post_type: post?.type || (mobile ? "mobile" : "fixed"),
      description: post?.description || "",
      is_armed: !!post?.is_armed,
      orders: "",
    };
    reset(values);
    setOrdersInfo(null);
    if (!post) return;
    // The list doesn't include orders, activities, or coverage; load them here.
    authFetch(`${process.env.REACT_APP_API_URL}/v1/posts/${post.id}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((full) => {
        if (!full) return;
        setPostActivities(full.activities || []);
        setCoverage(full.coverage || []);
        if (!full.orders) return;
        reset({ ...values, orders: full.orders.body });
        setOrdersInfo(full.orders);
      })
      .catch((error) => console.error("Error fetching post orders", error));
  }, [isOpen, post, mobile, reset, authFetch]);

  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const save = async (data) => {
    const url = isAddMode
      ? `${process.env.REACT_APP_API_URL}/v1/posts`
      : `${process.env.REACT_APP_API_URL}/v1/posts/${post.id}`;
    const { orders, ...fields } = data;
    const body = isAddMode && !mobile ? { ...fields, site_id: siteId } : fields;
    try {
      const response = await authFetch(url, {
        method: isAddMode ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Status ${response.status}`);
      }
      const saved = await response.json();
      // Orders are versioned separately; the server skips unchanged text.
      if (orders || ordersInfo) {
        const ordersResponse = await authFetch(
          `${process.env.REACT_APP_API_URL}/v1/posts/${saved.id}/orders`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ body: orders || "" }),
          }
        );
        if (!ordersResponse.ok) throw new Error("Saved, but post orders failed");
      }
      // Activities and coverage replace the saved lists; skip them on a new post
      // that has none.
      const putList = async (path, body) => {
        const res = await authFetch(
          `${process.env.REACT_APP_API_URL}/v1/posts/${saved.id}/${path}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(`Saved, but ${path}: ${err.error || "failed"}`);
        }
      };
      // Rows left without a tour/checkpoint picked are ignored.
      const chosenActivities = postActivities.filter((d) => d.activity_id);
      if (!isAddMode || chosenActivities.length) {
        await putList("activities", {
          activities: chosenActivities.map((d) => ({
            activity_type: d.activity_type,
            activity_id: d.activity_id,
            frequency_hours: d.frequency_hours,
          })),
        });
      }
      if (!isAddMode || coverage.length) {
        await putList("coverage", { coverage });
      }
      // The post exists now, so upload anything attached while creating it.
      // A failed file doesn't undo the post; it's reported and can be re-added.
      const failed = [];
      for (const file of pendingFiles) {
        try {
          await uploadFile(authFetch, { postId: saved.id }, file);
        } catch (error) {
          failed.push(file.name);
        }
      }
      setToast(
        failed.length
          ? {
              show: true,
              message: `Created ${noun}, but ${failed.join(", ")} didn't upload`,
              type: "danger",
            }
          : {
              show: true,
              message: isAddMode ? `Created ${noun}` : `Updated ${noun}`,
              type: "success",
            }
      );
      onSaved();
      onClose();
    } catch (error) {
      console.error(`Error saving ${noun}:`, error);
      setToast({ show: true, message: error.message, type: "danger" });
    }
  };

  return (
    <div>
      <FormProvider {...methods}>
        <SideSheet
          isOpen={isOpen}
          onClose={onClose}
          title={isAddMode ? `Create ${noun}` : `Edit ${noun}`}
          isAddMode={isAddMode}
          footerText={isAddMode ? `Create ${noun}` : `Update ${noun}`}
          handleSave={handleSubmit(save)}
        >
          <div className="flex-1 px-4 sm:px-6 py-6">
            <div className="space-y-6">
              <TextInput
                name="post_name"
                label="Name"
                placeholder={mobile ? "North route" : "Front desk"}
              />
              {!mobile && (
                <Select
                  name="post_type"
                  label="Type"
                  useForm
                  options={SITE_POST_TYPES}
                />
              )}
              <Checkbox
                name="is_armed"
                label="Armed"
                description="Guards covering this need to be armed."
              />
              <TextArea name="description" label="Description" />
              <PostCoverage coverage={coverage} onChange={setCoverage} />
              <PostActivities
                siteId={mobile ? null : siteId || post?.site?.id}
                activities={postActivities}
                onChange={setPostActivities}
              />
              <div>
                <TextArea
                  name="orders"
                  label="Post orders"
                  rows={10}
                  placeholder="What the guard does at this post: duties, access rules, patrol frequency, emergency steps."
                />
                {ordersInfo && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {`Last changed ${new Date(ordersInfo.updated_at).toLocaleDateString()}${
                      ordersInfo.updated_by?.name ? ` by ${ordersInfo.updated_by.name}` : ""
                    }`}
                  </p>
                )}
              </div>
              {post ? (
                <Files postId={post.id} />
              ) : (
                <Files
                  pendingFiles={pendingFiles}
                  onPendingChange={setPendingFiles}
                />
              )}
            </div>
          </div>
        </SideSheet>
      </FormProvider>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onHide={() => setToast((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}

export { PostDetails };
